/**
 * Validation utilities for checkout forms
 */

import { parsePhoneNumber } from "@baluu/ui/lib/country-codes";

// Email validation regex. The local part follows the RFC 5322 character set;
// the domain must contain at least one dot and end in a 2+ character
// alphabetic TLD. This rejects no-TLD addresses like "user@gmail" — the
// previous pattern made the TLD group optional (`*`) and let them through.
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

// Phone validation - at least 7 digits (excluding country code)
const PHONE_REGEX = /^\+?\d{1,4}[\s-]?\d{6,14}$/;

// Min/max length constants
const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 50;
const NATIONAL_NUMBER_MIN_DIGITS = 4;
const NATIONAL_NUMBER_MAX_DIGITS = 14;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

type ValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Validate email address
 */
function validateEmail(email: string): ValidationResult {
  if (!email || email.trim() === "") {
    return { isValid: false, error: "Email is required" };
  }

  const trimmed = email.trim();

  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }

  return { isValid: true };
}

/**
 * Validate phone number.
 * Expects a full E.164-style string (e.g. "+447911123456").
 * Extracts the national number and validates its length independently
 * of the dial code, since different countries have different lengths.
 */
function validatePhone(phone: string): ValidationResult {
  if (!phone || phone.trim() === "") {
    return { isValid: false, error: "Phone number is required" };
  }

  const { nationalNumber } = parsePhoneNumber(phone);
  const nationalDigits = nationalNumber.replace(/\D/g, "");

  if (nationalDigits.length < NATIONAL_NUMBER_MIN_DIGITS) {
    return {
      isValid: false,
      error: "Please enter a valid phone number",
    };
  }

  if (nationalDigits.length > NATIONAL_NUMBER_MAX_DIGITS) {
    return {
      isValid: false,
      error: "Phone number is too long",
    };
  }

  return { isValid: true };
}

/**
 * Validate first or last name
 */
function validateName(name: string, fieldName: string): ValidationResult {
  if (!name || name.trim() === "") {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmed = name.trim();

  if (trimmed.length < NAME_MIN_LENGTH) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${NAME_MIN_LENGTH} character`,
    };
  }

  if (trimmed.length > NAME_MAX_LENGTH) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${NAME_MAX_LENGTH} characters`,
    };
  }

  return { isValid: true };
}

/** The school's built-in buyer-phone checkout config — currently just the
 * `phone` row's `askAtCheckout`/`isRequired` flags (see `CheckoutBuyerConfig`
 * in `lib/checkout/api.ts`, which this mirrors). */
type BuyerPhoneConfig = {
  askAtCheckout: boolean;
  isRequired: boolean;
};

/**
 * Whether the buyer-phone field should be format-validated at submit time.
 * Single source of truth for the widget basket checkout and the embedded
 * BasketCheckoutOverlay, which both gate `validatePhone` on this — extracted
 * so the rule can't silently diverge between the two copies.
 *
 * Not asked at all → never enforce, regardless of what's in `phone` (a
 * school that turned the field off shouldn't have a stray value block
 * checkout). Asked + required → always enforce. Asked + optional → enforce
 * only once the buyer has actually SUPPLIED a number, so an untouched
 * optional field passes but a filled-but-malformed one still fails.
 *
 * "Supplied" is deliberately NOT `phone.trim() !== ""`. `PhoneCountryInput`
 * only normalizes a dial-code-only value back to `""` on its DIGIT-input
 * path (`handleNumberChange`) — its country-select path
 * (`handleCountrySelect`) emits `${dialCode}${nationalNumber}` unconditionally,
 * so opening the dropdown and picking a country with no digits typed yields a
 * non-empty string like `"+44"`. A raw non-emptiness check would then enforce
 * `validatePhone` on that value, and `"+44"` has zero national digits and
 * fails it — blocking payment on an OPTIONAL field that shows no asterisk,
 * with no way to recover except typing then deleting a digit. Extract the
 * national number via `parsePhoneNumber` (the same helper
 * `PhoneCountryInput` itself uses) and require at least one actual digit.
 */
function shouldEnforceBuyerPhone(
  config: BuyerPhoneConfig,
  phone: string,
): boolean {
  if (!config.askAtCheckout) return false;
  if (config.isRequired) return true;
  const { nationalNumber } = parsePhoneNumber(phone);
  return nationalNumber.replace(/\D/g, "").length > 0;
}

/**
 * Format phone number for display (add spaces)
 */
function formatPhoneNumber(phone: string): string {
  if (!phone) return "";

  // Remove all non-digit and non-plus characters
  const cleaned = phone.replace(/[^\d+]/g, "");

  // If starts with country code, format accordingly
  if (cleaned.startsWith("+")) {
    // Find where country code ends (1-4 digits after +)
    const match = cleaned.match(/^(\+\d{1,4})(\d+)$/);
    if (match) {
      const [, countryCode, number] = match;
      // Format number in groups of 3-4 digits
      const formatted = number?.replace(/(\d{3,4})(?=\d)/g, "$1 ") || "";
      return `${countryCode} ${formatted}`.trim();
    }
  }

  // Just add spaces every 3-4 digits
  return cleaned.replace(/(\d{3,4})(?=\d)/g, "$1 ");
}

/**
 * Validate gift/discount code format
 */
function validateDiscountCode(code: string): ValidationResult {
  if (!code || code.trim() === "") {
    return { isValid: false, error: "Please enter a code" };
  }

  const trimmed = code.trim();

  if (trimmed.length < 3) {
    return { isValid: false, error: "Code is too short" };
  }

  if (trimmed.length > 20) {
    return { isValid: false, error: "Code is too long" };
  }

  return { isValid: true };
}

/**
 * Format discount/gift card code (preserve case, just trim)
 */
function formatDiscountCode(code: string): string {
  if (!code) return "";

  // Just trim whitespace, preserve case since backend is case sensitive
  return code.trim();
}

type GuestValidationErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

/**
 * Validate all guest fields
 */
function validateGuest(
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
  },
  options: {
    isFirstGuest?: boolean;
    requirePhone?: boolean;
  } = {},
): GuestValidationErrors {
  const errors: GuestValidationErrors = {};
  const { isFirstGuest = true, requirePhone = true } = options;

  const firstNameResult = validateName(guest.firstName, "First name");
  if (!firstNameResult.isValid) {
    errors.firstName = firstNameResult.error;
  }

  const lastNameResult = validateName(guest.lastName, "Last name");
  if (!lastNameResult.isValid) {
    errors.lastName = lastNameResult.error;
  }

  // Email is required for first guest, optional for others
  if (isFirstGuest || guest.email) {
    const emailResult = validateEmail(guest.email);
    if (!emailResult.isValid) {
      errors.email = emailResult.error;
    }
  }

  // Phone is required for first guest only
  if (isFirstGuest && requirePhone) {
    const phoneResult = validatePhone(guest.phoneNumber || "");
    if (!phoneResult.isValid) {
      errors.phoneNumber = phoneResult.error;
    }
  }

  return errors;
}

/**
 * Check if guest has any validation errors
 */
function hasGuestErrors(errors: GuestValidationErrors): boolean {
  return Object.values(errors).some((error) => !!error);
}

export {
  validateEmail,
  validatePhone,
  validateName,
  validateDiscountCode,
  validateGuest,
  hasGuestErrors,
  formatPhoneNumber,
  formatDiscountCode,
  shouldEnforceBuyerPhone,
  EMAIL_REGEX,
  PHONE_REGEX,
};
export type { ValidationResult, GuestValidationErrors, BuyerPhoneConfig };

